// Curated list of IANA timezones for the intake + Portal dropdowns (#660).
//
// Full IANA list is ~600 names — too long for a dropdown. This list
// covers the EU primary market + the most common regions a family
// member would pick on behalf of a relative living abroad. Native-
// speaking-elderly users outside this list are still supported via the
// "Other..." option that lets the family type a raw IANA name (rare
// path but kept open so we don't have to ship a new release when a
// snowbird moves).
//
// Grouped by region for visual scanning. The IANA name is the value;
// the label includes the city + a current-DST-aware UTC offset hint
// computed client-side at render time (we don't hardcode offsets here
// because they drift twice a year).
//
// Keep alphabetical within each group so adding a new entry has an
// obvious home.
export const TIMEZONE_GROUPS = [
  {
    label: 'Europe',
    zones: [
      'Europe/Amsterdam',
      'Europe/Athens',
      'Europe/Berlin',
      'Europe/Brussels',
      'Europe/Bucharest',
      'Europe/Budapest',
      'Europe/Copenhagen',
      'Europe/Dublin',
      'Europe/Helsinki',
      'Europe/Lisbon',
      'Europe/London',
      'Europe/Madrid',
      'Europe/Oslo',
      'Europe/Paris',
      'Europe/Prague',
      'Europe/Rome',
      'Europe/Stockholm',
      'Europe/Vienna',
      'Europe/Warsaw',
      'Europe/Zurich',
    ],
  },
  {
    label: 'Americas',
    zones: [
      'America/Argentina/Buenos_Aires',
      'America/Chicago',
      'America/Denver',
      'America/Halifax',
      'America/Los_Angeles',
      'America/Mexico_City',
      'America/New_York',
      'America/Phoenix',
      'America/Sao_Paulo',
      'America/Toronto',
      'America/Vancouver',
    ],
  },
  {
    label: 'Asia & Middle East',
    zones: [
      'Asia/Dubai',
      'Asia/Hong_Kong',
      'Asia/Jerusalem',
      'Asia/Kolkata',
      'Asia/Seoul',
      'Asia/Shanghai',
      'Asia/Singapore',
      'Asia/Tokyo',
    ],
  },
  {
    label: 'Africa',
    zones: [
      'Africa/Cairo',
      'Africa/Johannesburg',
      'Africa/Lagos',
    ],
  },
  {
    label: 'Oceania',
    zones: [
      'Australia/Melbourne',
      'Australia/Perth',
      'Australia/Sydney',
      'Pacific/Auckland',
    ],
  },
  {
    label: 'Other',
    zones: ['UTC'],
  },
];

// Flat list for membership checks (e.g. "is the browser-detected zone
// in our curated set, or do we need to surface it separately?").
export const ALL_KNOWN_ZONES = TIMEZONE_GROUPS.flatMap(g => g.zones);

// Format an IANA name into a human-friendly dropdown label that
// includes the current UTC offset for the user's local time. Browser
// tzdata handles DST automatically; we compute the offset at render
// time so spring/autumn transitions are reflected next refresh
// without us shipping new code.
export function labelForZone(zoneName) {
  let offsetLabel = '';
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: zoneName,
      timeZoneName: 'shortOffset',
    });
    const parts = fmt.formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    if (tzPart && tzPart.value) offsetLabel = ` (${tzPart.value})`;
  } catch (_) {
    // Browser doesn't know this zone — surface the bare name without
    // an offset hint rather than swallowing the option entirely.
  }
  // Drop the region prefix for compactness: "Europe/Amsterdam" → "Amsterdam".
  // Two-segment names ("Asia/Tokyo") become "Tokyo"; three-segment
  // ("America/Argentina/Buenos_Aires") become "Buenos Aires" so the
  // most specific part stays visible.
  const segments = zoneName.split('/');
  const city = segments[segments.length - 1].replace(/_/g, ' ');
  return `${city}${offsetLabel}`;
}

// Best-effort browser detection. Returns the IANA zone if the browser
// supports `Intl.DateTimeFormat().resolvedOptions().timeZone` (every
// evergreen browser since ~2019), otherwise null.
export function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_) {
    return null;
  }
}
